import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useMapViewPersistence } from './useMapViewPersistence';
import {
  DEMO_LAYERS, DEMO_ANOMALIES,
  buildLineGeoJSON, buildElectrodeGeoJSON, buildSoundingGeoJSON,
  buildAnomalyGeoJSON, buildLineLabelGeoJSON, computeXyzStats,
} from '../lib/mapViewHelpers';
import { loadLayerOrder, saveLayerOrder } from '../lib/layer-order-store';
import { scalePaintOpacity } from '../lib/layer-tree';
import { stylePaintFor } from '../lib/layer-style';

/**
 * Layer state for MapView: demo/imported/result layers, per-layer styles,
 * chart filter, XYZ Z-field pipeline, and the memoised GeoJSON builders.
 */
export function useMapLayers({ mapRefHolder, usgsData, projectId, demoEnabled = true }) {
  const [layers, setLayers] = useState(demoEnabled ? DEMO_LAYERS : []);
  // The Cape Cod demo layers only belong to the Cape Cod project or to no project at all.
  useEffect(() => {
    setLayers(prev => (demoEnabled ? (prev.length ? prev : DEMO_LAYERS) : (prev.length ? [] : prev)));
  }, [demoEnabled]);
  const [importedLayers, setImportedLayers] = useState([]);
  const [resultLayers, setResultLayers] = useState([]);
  const [campaignFilter, setCampaignFilter] = useState('all');
  const [chartFilter, setChartFilter] = useState(null);   // { layerId, attr, values?|range? }
  const [styleTargetLayer, setStyleTargetLayer] = useState(null);
  const [layerStyles, setLayerStyles] = useState({});     // { [chartLayerId]: styleObj }
  const [layerOpacity, setLayerOpacityState] = useState({}); // { [chartLayerId]: 0..100 } imported/result layers
  const [activeLayerKey, setActiveLayerKey] = useState(null); // layer-tree entry key (lib/layer-tree.js)

  // Draw order: entry keys bottom to top (lib/layer-tree.js), persisted per project. Entries the
  // order has not seen yet are slotted in by default rank (normalizeOrder), so this may be stale.
  const [layerOrder, setLayerOrderState] = useState(() => loadLayerOrder(projectId));
  const orderProjectRef = useRef(projectId);
  useEffect(() => {
    if (orderProjectRef.current === projectId) return;
    orderProjectRef.current = projectId;
    setLayerOrderState(loadLayerOrder(projectId));
  }, [projectId]);
  const setLayerOrder = useCallback((next) => {
    setLayerOrderState(next);
    saveLayerOrder(projectId, next);
  }, [projectId]);
  const setLayerOpacity = useCallback((id, v) => setLayerOpacityState(prev => ({ ...prev, [id]: v })), []);

  // ── Preserve the view across "Update now" reloads (gis:before-update) ──
  const layersRef = useRef(layers);
  layersRef.current = layers;
  const getViewForUpdate = useCallback(() => {
    const map = mapRefHolder.current?.current?.getMap?.();
    if (!map) return null;
    const c = map.getCenter();
    return {
      latitude: c.lat, longitude: c.lng, zoom: map.getZoom(),
      bearing: map.getBearing(), pitch: map.getPitch(),
      layerIds: layersRef.current.filter(l => l.visible).map(l => l.id),
    };
  }, [mapRefHolder]);
  const restoredView = useMapViewPersistence(getViewForUpdate);
  useEffect(() => {
    if (!restoredView) return;
    if (Array.isArray(restoredView.layerIds)) {
      const ids = new Set(restoredView.layerIds);
      setLayers(ls => ls.map(l => ({ ...l, visible: ids.has(l.id) })));
    }
    // Bearing/pitch aren't SurveyMap props; apply once the map instance exists.
    let tries = 0;
    const t = setInterval(() => {
      const map = mapRefHolder.current?.current?.getMap?.();
      if (map) {
        map.jumpTo({ bearing: restoredView.bearing || 0, pitch: restoredView.pitch || 0 });
        clearInterval(t);
      } else if (++tries > 25) clearInterval(t);
    }, 200);
    return () => clearInterval(t);
  }, [restoredView, mapRefHolder]);

  // Fit map to all loaded data (F key / status-bar zoom click)
  const fitToData = useCallback(() => {
    const map = mapRefHolder.current?.current?.getMap?.();
    if (!map) return;
    let minLon = Infinity, minLat = Infinity, maxLon = -Infinity, maxLat = -Infinity, found = 0;
    const scan = (coords) => {
      if (typeof coords[0] === 'number') {
        if (coords[0] < minLon) minLon = coords[0];
        if (coords[0] > maxLon) maxLon = coords[0];
        if (coords[1] < minLat) minLat = coords[1];
        if (coords[1] > maxLat) maxLat = coords[1];
        found++;
      } else coords.forEach(scan);
    };
    [...Object.values(usgsData), ...importedLayers.map(l => l.geojson), ...resultLayers.map(r => r.geojson)]
      .forEach(gj => (gj?.features || []).slice(0, 20000).forEach(f => f.geometry?.coordinates && scan(f.geometry.coordinates)));
    if (found > 1 && isFinite(minLon)) {
      map.fitBounds([[minLon, minLat], [maxLon, maxLat]], { padding: 60, duration: 600 });
    }
  }, [usgsData, importedLayers, resultLayers, mapRefHolder]);

  // Toggle layer visibility
  const toggleLayer = (id) => setLayers(ls => ls.map(l => l.id === id ? { ...l, visible: !l.visible } : l));
  const setOpacity = (id, v) => setLayers(ls => ls.map(l => l.id === id ? { ...l, opacity: v } : l));

  // Rebuilt only when the underlying layer sets change — avoids rebuilding
  // these arrays (and handing SurveyMap new references) on every render,
  // e.g. on mousemove-driven re-renders (mouseCoord/xyzHover updates).
  const interactiveLayerIds = React.useMemo(() => [
    'survey-lines', 'soundings', 'anomaly-circles',
    // Chartable data layers (only include ids that are actually rendered)
    ...Object.entries(usgsData)
      .filter(([lid, gj]) => layers.find(l => l.id === lid)?.visible && gj?.features)
      .flatMap(([lid]) => [`usgs-pts-${lid}`, `usgs-line-${lid}`]),
    ...importedLayers.filter(l => l.visible).map(l => `imp-pts-${l.id}`),
    ...resultLayers.filter(r => r.visible && r.geojson?.features?.[0]?.geometry?.type === 'Point').map(r => `result-pts-${r.id}`),
  ], [usgsData, layers, importedLayers, resultLayers]);

  // ── Chart ↔ map linking (G35) ──
  const chartableLayers = React.useMemo(() => [
    ...Object.entries(usgsData)
      .filter(([lid, gj]) => gj?.features?.length)
      .map(([lid, gj]) => ({ id: `usgs-${lid}`, name: layers.find(l => l.id === lid)?.name || lid, geojson: gj })),
    ...importedLayers.map(l => ({ id: l.id, name: l.name, geojson: l.geojson })),
    ...resultLayers.map(r => ({ id: r.id, name: r.name, geojson: r.geojson })),
  ], [usgsData, layers, importedLayers, resultLayers]);

  // ── XYZ data pipeline: stats + colored paint for imported XYZ layers ──
  const xyzStats = React.useMemo(() => computeXyzStats(importedLayers), [importedLayers]);

  const xyzPaint = (l) => {
    const s = xyzStats[l.id];
    if (!s) return null;
    const { min } = s;
    const max = s.max > s.min ? s.max : s.min + 1;
    const t = (f) => min + (max - min) * f;
    return {
      'circle-radius': 5,
      'circle-color': ['interpolate', ['linear'], ['to-number', ['get', l.activeZ], min],
        min, '#2563eb', t(0.25), '#00b8de', t(0.5), '#34d399', t(0.75), '#fbbf24', max, '#f87171'],
      'circle-opacity': 0.9,
      'circle-stroke-color': '#0b1220',
      'circle-stroke-width': 0.8,
    };
  };

  const setLayerActiveZ = (layerId, z) => {
    setImportedLayers(prev => prev.map(l => l.id === layerId ? { ...l, activeZ: z } : l));
  };

  // Grid math: add a derived Z field computed from existing fields
  const deriveZField = (layerId, fieldName, compute) => {
    setImportedLayers(prev => prev.map(l => {
      if (l.id !== layerId || !l.xyz) return l;
      const features = l.geojson.features.map(f => {
        const v = compute(f.properties);
        return v === null ? f : { ...f, properties: { ...f.properties, [fieldName]: v } };
      });
      return {
        ...l,
        geojson: { ...l.geojson, features },
        xyz: { ...l.xyz, zFields: [...l.xyz.zFields, fieldName] },
        activeZ: fieldName,
      };
    }));
  };

  // Toggle a point's exclusion from gridding (right-click in attribute table)
  const toggleExcludeRow = (layerId, rowIdx) => {
    setImportedLayers(prev => prev.map(l => {
      if (l.id !== layerId) return l;
      const features = l.geojson.features.map(f =>
        f.properties?._row === rowIdx
          ? { ...f, properties: { ...f.properties, _excluded: !f.properties._excluded } }
          : f
      );
      return { ...l, geojson: { ...l.geojson, features } };
    }));
  };

  // Per-layer style overrides from the properties panel (lib/layer-style.js), scaled by the
  // layer-tree opacity slider (0..100). Project layers bake opacity into their default paint,
  // so it only applies on top of a custom style.
  const stylePaint = (chartLayerId, kind, defaults) => {
    let pct = layerOpacity[chartLayerId];
    if (chartLayerId.startsWith('usgs-')) {
      pct = layerStyles[chartLayerId] ? layers.find(l => `usgs-${l.id}` === chartLayerId)?.opacity : undefined;
    }
    return scalePaintOpacity(stylePaintFor(layerStyles[chartLayerId], kind, defaults), kind, pct == null ? undefined : pct / 100);
  };

  const applyLayerStyle = (chartLayerId, styleObj) => {
    setLayerStyles(prev => {
      if (styleObj === null) {
        const next = { ...prev };
        delete next[chartLayerId];
        return next;
      }
      return { ...prev, [chartLayerId]: styleObj };
    });
  };

  // Combine an optional geometry-type filter with the active chart filter (expression syntax)
  const withChartFilter = (chartLayerId, geomType) => {
    const parts = [];
    if (geomType) parts.push(['==', ['geometry-type'], geomType]);
    if (chartFilter && chartFilter.layerId === chartLayerId) {
      const { attr, values, range } = chartFilter;
      if (range && Number.isFinite(Number(range[0])) && Number.isFinite(Number(range[1]))) {
        // Numeric bounds only: a null bound makes MapLibre warn "found null" (V.23)
        parts.push(['>=', ['to-number', ['get', attr], -1e15], Number(range[0])]);
        parts.push(['<', ['to-number', ['get', attr], -1e15], Number(range[1])]);
      } else if (values) {
        parts.push(['in', ['to-string', ['get', attr]], ['literal', values.map(String)]]);
      }
    }
    if (parts.length === 0) return undefined;
    if (parts.length === 1) return parts[0];
    return ['all', ...parts];
  };

  // Build GeoJSON — memoized so these keep a stable object identity across
  // unrelated re-renders (e.g. mousemove-driven state updates). Source
  // components hand `data` straight to MapLibre's GeoJSON source, which
  // reloads/reparses on every new reference, so recreating these every
  // render was forcing needless source reloads while just hovering the map.
  const lineData = React.useMemo(() => buildLineGeoJSON(layers), [layers]);
  const electrodeData = React.useMemo(() => buildElectrodeGeoJSON(layers), [layers]);
  const soundingData = React.useMemo(() => buildSoundingGeoJSON(layers), [layers]);
  const anomalyData = React.useMemo(() => buildAnomalyGeoJSON(demoEnabled ? DEMO_ANOMALIES : []), [demoEnabled]);
  const lineLabelData = React.useMemo(() => buildLineLabelGeoJSON(layers), [layers]);

  // Campaign grouping
  const campaigns = [...new Set(layers.map(l => l.campaign).filter(Boolean))];
  const filteredLayers = campaignFilter === 'all' ? layers : layers.filter(l => l.campaign === campaignFilter);

  // "Set as active layer" (layer tree): the attribute table follows it.
  const activeImported = activeLayerKey?.startsWith('imp:') ? importedLayers.find(l => `imp:${l.id}` === activeLayerKey) : null;
  const activeXyzLayer = activeImported?.xyz ? activeImported : [...importedLayers].reverse().find(l => l.visible && l.xyz);
  const activeTableLayer = React.useMemo(() => {
    if (!activeLayerKey || activeImported?.xyz) return null;
    const [kind, ...rest] = activeLayerKey.split(':');
    const id = rest.join(':');
    const src = kind === 'imp' ? importedLayers.find(l => l.id === id)
      : kind === 'res' ? resultLayers.find(r => r.id === id)
        : kind === 'proj' ? { name: layers.find(l => l.id === id)?.name, geojson: usgsData[id] } : null;
    return src?.geojson?.features ? { name: src.name, features: src.geojson.features } : null;
  }, [activeLayerKey, activeImported, importedLayers, resultLayers, layers, usgsData]);

  return {
    layers, setLayers, importedLayers, setImportedLayers, resultLayers, setResultLayers,
    campaignFilter, setCampaignFilter, chartFilter, setChartFilter,
    styleTargetLayer, setStyleTargetLayer, layerStyles, setLayerStyles, restoredView, fitToData,
    toggleLayer, setOpacity, interactiveLayerIds, chartableLayers, xyzStats, xyzPaint,
    setLayerActiveZ, deriveZField, toggleExcludeRow, stylePaint, applyLayerStyle,
    withChartFilter, lineData, electrodeData, soundingData, anomalyData, lineLabelData,
    campaigns, filteredLayers, activeXyzLayer, activeTableLayer,
    layerOrder, setLayerOrder, layerOpacity, setLayerOpacity, activeLayerKey, setActiveLayerKey,
  };
}
